<div #box [class]="classes()" [style]="style()" aria-hidden="true">
  @switch (render()) {
    @case ('qr') {
      <svg
        class="qr"
        [attr.viewBox]="'0 0 ' + qr().size + ' ' + qr().size"
        preserveAspectRatio="xMidYMid meet"
        shape-rendering="crispEdges">
        <rect [attr.width]="qr().size" [attr.height]="qr().size" [attr.fill]="qrBackground()" />
        <path [attr.d]="qr().path" [attr.fill]="qrColor()" />
      </svg>
    }
    @case ('image') {
      @if (imageSrc(); as src) {
        <img #img [src]="src" alt="" draggable="false" [style.object-position]="objectPosition()" />
      } @else {
        <div class="placeholder">
          <ng-icon name="lucideImage" />
          <span class="placeholder-text">{{ placeholderText() }}</span>
        </div>
      }
    }
    @case ('box') {
      @if (drawnShape(); as shape) {
        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
          @if (shape === 'line') {
            <line
              [attr.x1]="lineIsWide() ? 0 : 50"
              [attr.y1]="lineIsWide() ? 50 : 0"
              [attr.x2]="lineIsWide() ? 100 : 50"
              [attr.y2]="lineIsWide() ? 50 : 100"
              [attr.stroke]="strokeColor()"
              [attr.stroke-width]="strokeWidth()"
              vector-effect="non-scaling-stroke"
              stroke-linecap="butt" />
          } @else {
            <polygon
              [attr.points]="polygon()"
              [attr.fill]="element().fill ?? 'none'"
              [attr.stroke]="strokeColor()"
              [attr.stroke-width]="strokeWidth()"
              vector-effect="non-scaling-stroke"
              stroke-linejoin="round" />
          }
        </svg>
      }
    }
    @case ('brand') {
      <img [src]="brandSrc()" alt="" draggable="false" />
    }
    @default {
      <!-- filled after render: the runs as text nodes and tinted placeholder spans -->
      <span></span>
    }
  }
</div>
<button
  type="button"
  class="surface"
  [class.outlined]="outlined()"
  [class.selected]="selected()"
  [class.snap-target]="snapTarget()"
  [class.editing]="editing()"
  [attr.aria-label]="ariaLabel()"
  [attr.aria-pressed]="selected()"
  (click)="onClick($event)"
  (dblclick)="onDoubleClick($event)"
  (pointerdown)="onSurfacePointerDown($event)"
  (keydown)="onKeydown($event)"></button>
@if (editing()) {
  <!-- the chip field over the text, in its font: typing where the text is -->
  <app-placeholder-field
    class="inline-editor"
    [plain]="true"
    [autofocus]="true"
    [selectAll]="selectAll()"
    [enterCommits]="!element().wrap"
    [content]="element().content ?? ''"
    [wrap]="!!element().wrap"
    [placeholders]="placeholders()"
    [elementId]="element().id"
    [style]="editorStyle()"
    (contentChange)="contentChange.emit({ id: element().id, content: $event })"
    (blurred)="editEnd.emit()"
    (pointerdown)="$event.stopPropagation()"
    (click)="$event.stopPropagation()"
    (dblclick)="$event.stopPropagation()" />
}
@if (selected() && !isText()) {
  <!-- a text needs no title: its values carry their names -->
  <span class="chip" aria-hidden="true">{{ label() }}</span>
}
<!-- Viewing shows the preview: samples with the name of each value over its run.
     Editing shows the template: the field's chips carry the names themselves. -->
@if (!editing()) {
  @for (value of valueLabels(); track $index) {
    <span
      class="value-label"
      aria-hidden="true"
      [style.left.pt]="value.x"
      [style.top.pt]="value.y"
      [style.transform]="labelTransform()"
      >{{ value.text }}</span
    >
  }
}
@if (showHandles() && canPan()) {
  <span class="hint" [style]="badgeStyle()" role="note">{{ panHint }}</span>
}
@if (badge(); as badge) {
  <button
    type="button"
    class="badge"
    [class.badge-error]="badge.severity === 'error'"
    [class.badge-warning]="badge.severity === 'warning'"
    [style]="badgeStyle()"
    [attr.aria-label]="(badge.severity === 'error' ? 'Fehler: ' : 'Hinweis: ') + badge.text"
    [hlmTooltip]="badge.text"
    position="right"
    (pointerdown)="$event.stopPropagation()"
    (click)="onClick($event); select.emit({ id: element().id, toggle: false })">
    <ng-icon [name]="badge.severity === 'error' ? 'lucideTriangleAlert' : 'lucideInfo'" />
    @if (badge.count > 1) {
      <span class="badge-count">{{ badge.count }}</span>
    }
  </button>
}
@if (showHandles()) {
  @for (handle of handles; track handle) {
    <button
      type="button"
      class="handle"
      [class]="'handle handle-' + handle"
      [attr.aria-label]="'Größe ändern: ' + handleLabels[handle]"
      (pointerdown)="onHandlePointerDown(handle, $event)"
      (keydown)="onHandleKeydown(handle, $event)"></button>
  }
}
